import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Fab,
  Alert,
  Snackbar,
  Chip,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import RouteIcon from '@mui/icons-material/Route';
import PlaceIcon from '@mui/icons-material/Place';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import api from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';

import { MapaVisitas } from '../components/MapaVisitas';
import { CheckinModal } from '../components/CheckinModal';
import { CheckoutModal } from '../components/CheckoutModal';
import { useGeolocation } from '../hooks/useGeolocation';
import { useClientesMapa } from '../hooks/useClientesMapa';
import { useVisitaAtiva } from '../hooks/useVisitaAtiva';
import { useOSRM } from '../hooks/useOSRM';
import type { ClienteMapa, Visita } from '../types/visita.types';

// AIDEV-NOTE: Pagina principal do mapa — compoe GPS, clientes, check-in/out e OSRM

interface GeoResult {
  processados: number;
  atualizados: number;
  erros: number;
  sem_endereco: number;
}

export function MapaPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { position, error: gpsError } = useGeolocation(true);
  const { data: clientes = [], isLoading: clientesLoading } = useClientesMapa();
  const { data: visitaAtiva } = useVisitaAtiva();

  const [selectedCliente, setSelectedCliente] = useState<ClienteMapa | null>(null);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [geoDialogOpen, setGeoDialogOpen] = useState(false);
  const [geoResult, setGeoResult] = useState<GeoResult | null>(null);
  const [activeGeoJobId, setActiveGeoJobId] = useState<string | null>(null);

  const geoJobQuery = useQuery({
    queryKey: ['geo-job', activeGeoJobId],
    enabled: !!activeGeoJobId,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2000 : false),
    queryFn: async () => {
      const res = await api.get<{ status: string; result?: GeoResult; error?: string }>(
        `/visitas/clientes/geocodificar/status/${activeGeoJobId}`
      );
      return res.data;
    }
  });

  // AIDEV-NOTE: captura result em variável local antes de mudar activeGeoJobId
  // para evitar que geoJobQuery.data mude referência após setActiveGeoJobId(null)
  useEffect(() => {
    const jobData = geoJobQuery.data;
    if (!jobData) return;
    if (jobData.status === 'completed') {
      const result = jobData.result;
      setActiveGeoJobId(null);
      setGeoResult(result ?? null);
      if (result && result.atualizados > 0) {
        void queryClient.invalidateQueries({ queryKey: ['clientes-mapa'] });
      }
    } else if (jobData.status === 'failed') {
      setActiveGeoJobId(null);
      setSnackbar({
        open: true,
        message: jobData.error ?? 'Erro ao geocodificar',
        severity: 'error'
      });
      setGeoDialogOpen(false);
    }
  }, [geoJobQuery.data, queryClient]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // AIDEV-NOTE: Mutation de geocodificacao — retorna 202+jobId; polling via geoJobQuery
  const geocodificarMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ jobId: string }>('/visitas/clientes/geocodificar?limite=30');
      return res.data;
    },
    onSuccess: ({ jobId }) => {
      setGeoResult(null);
      setActiveGeoJobId(jobId);
    },
    onError: (err) => {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Erro ao geocodificar',
        severity: 'error'
      });
      setGeoDialogOpen(false);
    }
  });

  // AIDEV-NOTE: Calcula coordenadas para rota OSRM — posicao do usuario + clientes agendados hoje
  const rotaCoords = useMemo(() => {
    if (!position) return undefined;
    const agendados = clientes.filter((c) => c.tem_agenda_hoje && c.lat != null && c.lng != null);
    if (agendados.length === 0) return undefined;

    return [
      { lat: position.lat, lng: position.lng },
      ...agendados.map((c) => ({ lat: c.lat!, lng: c.lng! }))
    ];
  }, [position, clientes]);

  const { data: rota } = useOSRM(rotaCoords);

  const handleCheckin = useCallback((cliente: ClienteMapa) => {
    setSelectedCliente(cliente);
    setCheckinOpen(true);
  }, []);

  const handleCheckinSuccess = useCallback(
    (_visita: Visita) => {
      setCheckinOpen(false);
      setSelectedCliente(null);
      queryClient.invalidateQueries({ queryKey: ['visita-ativa'] });
      setSnackbar({ open: true, message: 'Check-in realizado com sucesso!', severity: 'success' });
    },
    [queryClient]
  );

  const handleCheckoutSuccess = useCallback(() => {
    setCheckoutOpen(false);
    queryClient.invalidateQueries({ queryKey: ['visita-ativa'] });
    queryClient.invalidateQueries({ queryKey: ['clientes-mapa'] });
    setSnackbar({ open: true, message: 'Check-out realizado com sucesso!', severity: 'success' });
  }, [queryClient]);

  const handleCenterOnUser = useCallback(() => {
    // AIDEV-TODO: Implementar fly-to para posicao do usuario via ref do MapContainer
    // Por ora, recarrega a posicao via refresh
    if (!position) {
      setSnackbar({ open: true, message: 'GPS indisponivel', severity: 'error' });
    }
  }, [position]);

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, width: '100%' }}>
      {/* Alerta de GPS negado */}
      {gpsError && (
        <Alert
          severity="warning"
          sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 1000 }}
        >
          {gpsError.code === 1
            ? 'Permissao de GPS negada. Habilite nas configuracoes do navegador.'
            : `Erro de GPS: ${gpsError.message}`}
        </Alert>
      )}

      {/* Info da rota OSRM */}
      {rota && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: 'absolute',
            top: gpsError ? 64 : 8,
            left: 8,
            zIndex: 1000
          }}
        >
          <Chip
            icon={<RouteIcon />}
            label={`Rota: ${rota.distance} - ${rota.duration}`}
            color="primary"
            size="small"
            sx={{ bgcolor: 'white', boxShadow: 1 }}
          />
        </Stack>
      )}

      {/* Indicador de carregamento */}
      {clientesLoading && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            bgcolor: 'white',
            px: 2,
            py: 0.5,
            borderRadius: 1,
            boxShadow: 1
          }}
        >
          Carregando clientes...
        </Typography>
      )}

      {/* Botao geocodificar — disponivel para todos os perfis autenticados */}
      {user?.perfil && ['admin', 'gerente', 'vendedor', 'representante'].includes(user.perfil) && (
        <Box sx={{ position: 'absolute', top: gpsError ? 64 : 8, right: 8, zIndex: 1000 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={
              geocodificarMutation.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <PlaceIcon fontSize="small" />
              )
            }
            onClick={() => {
              setGeoResult(null);
              setGeoDialogOpen(true);
            }}
            disabled={geocodificarMutation.isPending}
            sx={{
              bgcolor: 'white',
              color: 'text.primary',
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            {geocodificarMutation.isPending ? 'Geocodificando...' : 'Geocodificar'}
          </Button>
        </Box>
      )}

      {/* Mapa */}
      <MapaVisitas
        clientes={clientes}
        userPosition={position ?? undefined}
        rota={rota ?? undefined}
        onCheckin={handleCheckin}
      />

      {/* FAB Minha Localizacao */}
      <Fab
        color="primary"
        size="medium"
        onClick={handleCenterOnUser}
        sx={{ position: 'absolute', bottom: 24, right: 24, zIndex: 1000 }}
        aria-label="Minha localizacao"
      >
        <MyLocationIcon />
      </Fab>

      {/* FAB Check-out (visivel quando ha visita ativa) */}
      {visitaAtiva && (
        <Fab
          variant="extended"
          color="secondary"
          onClick={() => setCheckoutOpen(true)}
          sx={{ position: 'absolute', bottom: 24, left: 24, zIndex: 1000 }}
        >
          Check-out
        </Fab>
      )}

      {/* Check-in Modal */}
      {selectedCliente && (
        <CheckinModal
          open={checkinOpen}
          onClose={() => {
            setCheckinOpen(false);
            setSelectedCliente(null);
          }}
          onSuccess={handleCheckinSuccess}
          cliente={selectedCliente}
        />
      )}

      {/* Check-out Modal */}
      {visitaAtiva && (
        <CheckoutModal
          open={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          onSuccess={handleCheckoutSuccess}
          visita={visitaAtiva}
        />
      )}

      {/* Dialog de geocodificacao — confirmacao e resultado */}
      <Dialog
        open={geoDialogOpen}
        onClose={() =>
          !activeGeoJobId && !geocodificarMutation.isPending && setGeoDialogOpen(false)
        }
      >
        <DialogTitle>Geocodificar Clientes</DialogTitle>
        <DialogContent>
          {geoResult ? (
            <Box>
              <DialogContentText gutterBottom>Processamento concluido:</DialogContentText>
              <Typography variant="body2">
                • Clientes processados: <strong>{geoResult?.processados ?? 0}</strong>
              </Typography>
              <Typography variant="body2" color="success.main">
                • Coordenadas adicionadas: <strong>{geoResult?.atualizados ?? 0}</strong>
              </Typography>
              {(geoResult?.erros ?? 0) > 0 && (
                <Typography variant="body2" color="warning.main">
                  • Enderecos nao encontrados: <strong>{geoResult?.erros}</strong>
                </Typography>
              )}
              {(geoResult?.sem_endereco ?? 0) > 0 && (
                <Typography variant="body2" color="text.secondary">
                  • Sem endereco cadastrado: <strong>{geoResult?.sem_endereco}</strong>
                </Typography>
              )}
              {geoResult?.processados === 30 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Limite de 30 clientes por rodada atingido. Clique em Geocodificar novamente para
                  continuar.
                </Alert>
              )}
            </Box>
          ) : activeGeoJobId ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
              <CircularProgress size={20} />
              <DialogContentText>Geocodificando clientes… aguarde.</DialogContentText>
            </Box>
          ) : (
            <DialogContentText>
              Isso buscara coordenadas GPS para os clientes sem localizacao cadastrada usando os
              enderecos do ERP (Nominatim/OpenStreetMap).
              <br />
              <br />
              Serao processados ate <strong>30 clientes por vez</strong>. O processo pode levar
              cerca de 30-40 segundos.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setGeoDialogOpen(false)}
            disabled={!!activeGeoJobId || geocodificarMutation.isPending}
          >
            {geoResult ? 'Fechar' : 'Cancelar'}
          </Button>
          {!geoResult && !activeGeoJobId && (
            <Button
              variant="contained"
              onClick={() => geocodificarMutation.mutate()}
              disabled={geocodificarMutation.isPending}
              startIcon={
                geocodificarMutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {geocodificarMutation.isPending ? 'Iniciando...' : 'Iniciar'}
            </Button>
          )}
          {geoResult && geoResult.processados === 30 && (
            <Button
              variant="contained"
              onClick={() => {
                setGeoResult(null);
                geocodificarMutation.mutate();
              }}
            >
              Proximos 30
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar de feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
